import { useState, useEffect } from "react";
import { useParams } from "react-router";
import { useApi, useApiMutation } from "@/hooks/useApi";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { IconPicker } from "@/components/shared/IconPicker";
import { LoadingSpinner } from "@/components/shared/LoadingSpinner";
import { showToast } from "@/components/shared/Toast";
import { Save, User, Palmtree, Calendar, Check, X, Power, Bell, KeyRound } from "lucide-react";
import { subscribeToPush, resubscribeToPush, sendSubscriptionToServer } from "@/lib/pwa";

interface HolidayRequest {
  client_key: string;
  client_name: string;
  client_icon: string;
  starts_at: string;
  ends_at: string;
  status: string;
  requested_at: string;
}

interface AdminProfile {
  _key: string;
  name: string;
  icon: string;
  description: string;
  holiday_mode: boolean;
  holiday_started_at: string | null;
  holiday_ends_at: string | null;
  holiday_started_by: string | null;
  pending_holiday_requests: HolidayRequest[];
}

export function AdminProfile() {
  const { jwt } = useParams();
  const { setAuth, user, getHeaders } = useAuth();

  const { data: profile, loading, error, refetch } = useApi<AdminProfile>(
    `/api/admin/${jwt}/profile`,
  );

  const { mutate, loading: saving, error: saveError } = useApiMutation<
    Partial<AdminProfile>,
    AdminProfile
  >(`/api/admin/${jwt}/profile`);

  const { mutate: mutateHoliday, loading: holidayLoading } = useApiMutation<
    { enabled: boolean; ends_at?: string },
    { holiday_mode: boolean }
  >(`/api/admin/${jwt}/holiday`);

  const [name, setName] = useState("");
  const [icon, setIcon] = useState("👑");
  const [description, setDescription] = useState("");
  const [holidayEndDate, setHolidayEndDate] = useState("");
  
  // Notification state
  const [notificationStatus, setNotificationStatus] = useState<"granted" | "denied" | "default" | "unsupported">("unsupported");
  const [notificationLoading, setNotificationLoading] = useState(false);

  // Passcode change state
  const [currentPasscode, setCurrentPasscode] = useState("");
  const [newPasscode, setNewPasscode] = useState("");
  const [confirmPasscode, setConfirmPasscode] = useState("");
  const [passcodeLoading, setPasscodeLoading] = useState(false);

  useEffect(() => {
    if (profile) {
      setName(profile.name);
      setIcon(profile.icon);
      setDescription(profile.description);
    }
  }, [profile]);

  useEffect(() => {
    if (typeof Notification !== "undefined") {
      setNotificationStatus(Notification.permission);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      showToast.error("Name is required");
      return;
    }

    const result = await mutate({ name, icon, description }, "PUT");
    if (result) {
      // Update session auth state so header reflects changes immediately
      setAuth({
        user: {
          _key: result._key,
          name: result.name,
          icon: result.icon,
          description: result.description,
        },
      });
      showToast.success("Profile updated successfully");
    } else if (saveError) {
      showToast.error(saveError);
    }
  };

  const handleChangePasscode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentPasscode || !newPasscode) {
      showToast.error("Please fill in all passcode fields");
      return;
    }
    if (newPasscode.length < 4) {
      showToast.error("New passcode must be at least 4 digits");
      return;
    }
    if (newPasscode !== confirmPasscode) {
      showToast.error("New passcodes do not match");
      return;
    }
    setPasscodeLoading(true);
    try {
      const res = await fetch(`/api/admin/${jwt}/change-passcode`, {
        method: "PUT",
        headers: getHeaders(),
        body: JSON.stringify({
          current_passcode: currentPasscode,
          new_passcode: newPasscode,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);

      // Update stored passcode in auth state
      setAuth({ passcode: newPasscode });
      setCurrentPasscode("");
      setNewPasscode("");
      setConfirmPasscode("");
      showToast.success("Passcode changed successfully");
    } catch (err) {
      showToast.error(err instanceof Error ? err.message : "Failed to change passcode");
    } finally {
      setPasscodeLoading(false);
    }
  };

  const handleToggleHoliday = async () => {
    if (profile?.holiday_mode) {
      // Deactivate
      const result = await mutateHoliday({ enabled: false }, "PUT");
      if (result) {
        showToast.success("Holiday mode deactivated — chores resumed!");
        refetch();
      }
    } else {
      // Activate
      if (!holidayEndDate) {
        showToast.error("Please select an end date");
        return;
      }
      const result = await mutateHoliday({ enabled: true, ends_at: new Date(holidayEndDate).toISOString() }, "PUT");
      if (result) {
        showToast.success("Holiday mode activated!");
        setHolidayEndDate("");
        refetch();
      }
    }
  };

  const handleHolidayRequest = async (clientKey: string, action: "approve" | "deny") => {
    try {
      const res = await fetch(`/api/admin/${jwt}/clients/${clientKey}/holiday-request`, {
        method: "PUT",
        headers: getHeaders(),
        body: JSON.stringify({ action }),
      });
      if (res.ok) {
        showToast.success(action === "approve" ? "Holiday request approved" : "Holiday request denied");
        refetch();
      } else {
        const data = await res.json().catch(() => ({}));
        showToast.error(data.error || "Failed to process request");
      }
    } catch {
      showToast.error("Failed to process request");
    }
  };

  const handleEnableNotifications = async (forceReregister = false) => {
    setNotificationLoading(true);
    try {
      // Fetch VAPID public key
      const vapidResp = await fetch("/api/push/vapid-public-key");
      if (!vapidResp.ok) {
        showToast.error("Failed to get push configuration");
        return;
      }
      const { publicKey } = await vapidResp.json();

      // Subscribe to push notifications
      const subscription = forceReregister
        ? await resubscribeToPush(publicKey)
        : await subscribeToPush(publicKey);
      if (!subscription) {
        if (Notification.permission === "granted") {
          showToast.error("Push notifications aren't supported by this browser. Try Chrome instead.");
        } else {
          showToast.error("Notification permission denied or unavailable");
        }
        setNotificationStatus(Notification.permission);
        return;
      }

      // Send subscription to server
      const success = await sendSubscriptionToServer(
        subscription,
        "admin",
        jwt!,
        getHeaders(),
      );

      if (success) {
        showToast.success(forceReregister ? "Notifications re-registered!" : "Notifications enabled successfully!");
        setNotificationStatus("granted");
      } else {
        showToast.error("Failed to save notification subscription");
      }
    } catch (err) {
      showToast.error("Failed to enable notifications");
      console.error(err);
    } finally {
      setNotificationLoading(false);
    }
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <p className="text-app-danger text-body p-4">{error}</p>;

  const hasChanges =
    profile &&
    (name !== profile.name ||
      icon !== profile.icon ||
      description !== profile.description);

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Profile Header */}
      <Card className="border-0 shadow-lg overflow-hidden py-0">
        <div className="bg-linear-to-br from-app-primary/20 via-app-accent/10 to-transparent p-6 md:p-8">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-app-surface-raised shadow-md flex items-center justify-center">
              <span className="text-3xl md:text-4xl">{icon}</span>
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-heading font-bold text-app-text truncate">
                {name || "Admin"}
              </h1>
              <p className="text-body text-app-text-muted mt-0.5">
                {description || "No description"}
              </p>
            </div>
          </div>
        </div>
      </Card>

      {/* Edit Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5" />
            Edit Profile
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Icon */}
            <div className="space-y-2">
              <Label>Icon</Label>
              <IconPicker value={icon} onSelect={setIcon} />
            </div>

            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="admin-name">Name</Label>
              <Input
                id="admin-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your display name"
                maxLength={50}
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="admin-desc">Description</Label>
              <Textarea
                id="admin-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="A short bio or description..."
                maxLength={200}
                rows={3}
              />
            </div>

            {/* Save Button */}
            <Button
              type="submit"
              disabled={saving || !hasChanges}
              className="w-full"
            >
              {saving ? (
                <span className="flex items-center gap-2">
                  <LoadingSpinner /> Saving...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Save className="w-4 h-4" /> Save Changes
                </span>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Change Passcode */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <KeyRound className="w-5 h-5" />
            Change Passcode
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangePasscode} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="admin-current-pass">Current Passcode</Label>
              <Input
                id="admin-current-pass"
                type="password"
                inputMode="numeric"
                pattern="[0-9]*"
                value={currentPasscode}
                onChange={(e) => setCurrentPasscode(e.target.value)}
                placeholder="Enter current passcode"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="admin-new-pass">New Passcode</Label>
              <Input
                id="admin-new-pass"
                type="password"
                inputMode="numeric"
                pattern="[0-9]*"
                value={newPasscode}
                onChange={(e) => setNewPasscode(e.target.value)}
                placeholder="At least 4 digits"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="admin-confirm-pass">Confirm New Passcode</Label>
              <Input
                id="admin-confirm-pass"
                type="password"
                inputMode="numeric"
                pattern="[0-9]*"
                value={confirmPasscode}
                onChange={(e) => setConfirmPasscode(e.target.value)}
                placeholder="Re-enter new passcode"
              />
            </div>
            <Button
              type="submit"
              disabled={passcodeLoading || !currentPasscode || !newPasscode || !confirmPasscode}
              className="w-full"
            >
              {passcodeLoading ? (
                <span className="flex items-center gap-2">
                  <LoadingSpinner /> Changing...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <KeyRound className="w-4 h-4" /> Change Passcode
                </span>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Holiday Mode */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Palmtree className="w-5 h-5" />
            Holiday Mode
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {profile?.holiday_mode ? (
            <>
              <div className="flex items-center gap-3 p-4 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
                <span className="text-2xl">🏖️</span>
                <div className="flex-1">
                  <p className="font-medium text-amber-800 dark:text-amber-200">
                    Holiday mode is active
                  </p>
                  <p className="text-sm text-amber-600 dark:text-amber-400">
                    All chores, rules, and notifications are paused.
                    {profile.holiday_ends_at && (
                      <> Ends {new Date(profile.holiday_ends_at).toLocaleDateString()}</>
                    )}
                  </p>
                </div>
              </div>
              <Button
                onClick={handleToggleHoliday}
                disabled={holidayLoading}
                variant="outline"
                className="w-full"
              >
                {holidayLoading ? (
                  <span className="flex items-center gap-2"><LoadingSpinner /> Ending...</span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Power className="w-4 h-4" /> End Holiday Mode
                  </span>
                )}
              </Button>
            </>
          ) : (
            <>
              <p className="text-sm text-app-text-muted">
                Activate holiday mode to pause all chores, streaks, and notifications for all your partners. An end date is required.
              </p>
              <div className="space-y-2">
                <Label htmlFor="holiday-end">End Date</Label>
                <Input
                  id="holiday-end"
                  type="date"
                  value={holidayEndDate}
                  onChange={(e) => setHolidayEndDate(e.target.value)}
                  min={new Date(Date.now() + 86400000).toISOString().split("T")[0]}
                />
              </div>
              <Button
                onClick={handleToggleHoliday}
                disabled={holidayLoading || !holidayEndDate}
                className="w-full"
              >
                {holidayLoading ? (
                  <span className="flex items-center gap-2"><LoadingSpinner /> Activating...</span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Palmtree className="w-4 h-4" /> Activate Holiday Mode
                  </span>
                )}
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      {/* Pending Holiday Requests */}
      {profile?.pending_holiday_requests && profile.pending_holiday_requests.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Holiday Requests
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {profile.pending_holiday_requests.map((req) => (
              <div
                key={req.client_key}
                className="flex items-center gap-3 p-3 rounded-lg bg-app-surface-raised border border-app-border"
              >
                <span className="text-xl">{req.client_icon}</span>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-app-text text-sm truncate">
                    {req.client_name}
                  </p>
                  <p className="text-xs text-app-text-muted">
                    {new Date(req.starts_at).toLocaleDateString()} → {new Date(req.ends_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex gap-1.5">
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-app-success hover:text-app-success h-8 w-8 p-0"
                    onClick={() => handleHolidayRequest(req.client_key, "approve")}
                  >
                    <Check className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-app-danger hover:text-app-danger h-8 w-8 p-0"
                    onClick={() => handleHolidayRequest(req.client_key, "deny")}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Notifications */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5" />
            Push Notifications
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {notificationStatus === "unsupported" ? (
            <p className="text-sm text-app-text-muted">
              Push notifications are not supported in your browser.
            </p>
          ) : notificationStatus === "granted" ? (
            <>
              <div className="flex items-center gap-3 p-4 rounded-lg bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800">
                <span className="text-2xl">🔔</span>
                <div className="flex-1">
                  <p className="font-medium text-green-800 dark:text-green-200">
                    Notifications enabled
                  </p>
                  <p className="text-sm text-green-600 dark:text-green-400">
                    You'll receive push notifications for partner updates, rule breaks, and messages.
                  </p>
                </div>
              </div>
              <Button
                onClick={() => handleEnableNotifications(true)}
                disabled={notificationLoading}
                variant="outline"
                className="w-full"
              >
                {notificationLoading ? (
                  <span className="flex items-center gap-2"><LoadingSpinner /> Re-registering...</span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Bell className="w-4 h-4" /> Re-register Notifications
                  </span>
                )}
              </Button>
            </>
          ) : notificationStatus === "denied" ? (
            <div className="flex items-center gap-3 p-4 rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800">
              <span className="text-2xl">🔕</span>
              <div className="flex-1">
                <p className="font-medium text-red-800 dark:text-red-200">
                  Notifications blocked
                </p>
                <p className="text-sm text-red-600 dark:text-red-400">
                  Enable notifications in your browser settings to receive alerts.
                </p>
              </div>
            </div>
          ) : (
            <>
              <p className="text-sm text-app-text-muted">
                Enable push notifications to get instant alerts for rule breaks, new messages, and daily briefings.
              </p>
              <Button
                onClick={() => handleEnableNotifications()}
                disabled={notificationLoading}
                className="w-full"
              >
                {notificationLoading ? (
                  <span className="flex items-center gap-2"><LoadingSpinner /> Enabling...</span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Bell className="w-4 h-4" /> Enable Notifications
                  </span>
                )}
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}